/**
 * Turning core-api's shapes into a thread.
 *
 * A contact's thread is their record's timeline (`GET /leads/{id}/timeline`) and, for a sender with
 * no record, that conversation's texts (ADR-095). These are the pure adapters between the two: one
 * entry or one text in, one `Activity` out, and a kind this build does not know left out rather
 * than drawn wrong. An email (ADR-180) comes the same way, a message of the `email` channel, and
 * carries its subject and first words only: the whole email is read when its line is opened.
 *
 * Split from `conversations.ts`, which holds the model and the page's own state, to keep both under
 * the file-size limit. Pure, for `test/conversations-timeline.test.ts`.
 */

import { callbackCallText } from "@/lib/callbacks";
import { noteTitle, stageChangeText, systemEventText, textMessageWords } from "@/lib/contacts";
import { callText, humanize } from "@/lib/lead-card";
import type {
  LeadCallback, LeadCard, LeadCardCall, LeadNote, LeadTimeline, LeadTimelineEntry, TextConversation, TextMessage,
} from "@/lib/types";
import {
  latestActivity, messageStatus, type Activity, type ActivityFilter, type CallActivity, type CallStatus,
  type ContactField, type Conversation, type FieldType, type MessageActivity, type Person, type UnreadAtOpen,
} from "@/lib/conversations";

/* --- from core-api's timeline ----------------------------------------------------------------- */
//
// A contact's thread is their record's timeline (`GET /leads/{id}/timeline`), in the thread's
// shapes. Calls, notes, stage moves, callbacks, the platform's own entries and texts; a kind this
// build does not know is left out rather than drawn wrong.

/**
 * How a call ended, in the thread's words. A stay in a line is timed: talk means it connected, and
 * none (ABANDONED, TIMEOUT, DROP) is missed. A dial that reached a rep is listed as its stay in the
 * line, so a dial on its own never connected: busy, failed, or else no answer — voicemail, a
 * screener, an IVR say so in the summary line. A call only the switch recorded (ACL-496) is timed
 * too, and one nobody talked on is a caller missed or a dial's ending.
 */
export function callStatus(call: Pick<LeadCardCall, "via" | "outcome" | "talk_seconds">): CallStatus {
  const outcome = call.outcome?.toLowerCase() ?? "";
  if (call.via === "queue") return call.talk_seconds ? "completed" : "missed";
  if (call.via === "direct") {
    if (call.talk_seconds) return "completed";
    if (outcome === "missed") return "missed";
  }
  switch (outcome) {
    case "busy":
      return "busy";
    case "failed":
      return "failed";
    default:
      return "no_answer";
  }
}

function person(id: number | string | null, name: string | null): Person | null {
  if (!name) return null;
  return { id: id === null ? name : String(id), name };
}

/** Who a note with no person behind it came from (`lead_notes.author`): the importer that carried
 *  GoHighLevel's notes over, or another system through a trigger (`lead.note`). */
const NOTE_SENDERS: Record<string, string> = { ghl: "GoHighLevel", trigger: "An integration" };

/** One timeline entry as a thread activity, or null for a kind (or a body) this build cannot draw.
 *  `label` turns an outcome's slug into the campaign's word for it. */
export function activityOf(
  entry: LeadTimelineEntry,
  label: (slug: string) => string | undefined = () => undefined,
): Activity | null {
  const base = { id: entry.key, at: entry.at };
  switch (entry.kind) {
    case "call": {
      const call = entry.call;
      if (!call) return null;
      const summary = callText(call, label);
      return {
        ...base,
        kind: "call",
        direction: call.direction === "inbound" ? "inbound" : "outbound",
        status: callStatus(call),
        durationSeconds: call.talk_seconds ? Math.round(call.talk_seconds) : null,
        agent: person(call.rep, call.rep_name ?? call.rep),
        callId: call.call_id ?? entry.call_id ?? null,
        hasRecording: call.has_recording ?? false,
        hasTranscript: call.has_transcript ?? false,
        outcome: call.disposition ? label(call.disposition) ?? humanize(call.disposition) : null,
        summary: call.kind === "handoff" ? `Handed off · ${summary}` : summary,
        forCallback: callbackCallText(call),
      };
    }
    case "note": {
      const note = entry.note;
      if (!note) return null;
      // A workflow's Add note step (ACL-370): a note nobody on the team wrote. Its title and author
      // are a rep's words; the thread names the workflow from `note.workflow` to a reader who sees
      // automation (ACL-591).
      if (note.kind === "system" && note.workflow) {
        return {
          ...base,
          kind: "comment",
          title: noteTitle(note, label),
          body: note.body,
          author: { id: `workflow:${note.workflow.id}`, name: "Automated" },
          note,
        };
      }
      // The platform's own remark: nobody on the team wrote it.
      if (note.kind === "system") return { ...base, kind: "event", text: note.body };
      return {
        ...base,
        kind: "comment",
        title: noteTitle(note, label),
        body: note.body,
        author: person(note.by_user_id, note.by) ?? {
          id: note.author,
          name: NOTE_SENDERS[note.author] ?? "Someone no longer here",
        },
        note,
      };
    }
    case "stage": {
      if (!entry.stage) return null;
      const words = stageChangeText(entry.stage);
      return {
        ...base,
        kind: "event",
        text: [`Stage ${words.title}`, words.detail, entry.by].filter(Boolean).join(" · "),
        workflow: entry.workflow ?? null,
      };
    }
    case "callback": {
      const callback = entry.callback;
      if (!callback) return null;
      const outcome = label(callback.disposition) ?? humanize(callback.disposition) ?? "Callback";
      return {
        ...base,
        kind: "event",
        text: [`Callback · ${outcome}`, callback.purpose, callback.by && `set by ${callback.by}`]
          .filter(Boolean)
          .join(" · "),
        callback,
      };
    }
    case "system":
      return entry.event ? { ...base, kind: "event", text: systemEventText(entry.event) } : null;
    case "message":
      return entry.message ? messageActivity(entry.message, person(entry.by_user_id, entry.by), entry.key) : null;
    default:
      return null;
  }
}

/**
 * The thread from the timeline's pages, oldest first as a thread reads. core-api serves each page
 * newest first and each further page older, so the pages flattened are newest-to-oldest; an entry
 * that two pages both carry (one landed between fetches) is kept once.
 */
export function threadActivities(
  pages: readonly Pick<LeadTimeline, "entries">[],
  label?: (slug: string) => string | undefined,
): Activity[] {
  const seen = new Set<string>();
  const out: Activity[] = [];
  for (const page of pages) {
    for (const entry of page.entries) {
      if (seen.has(entry.key)) continue;
      seen.add(entry.key);
      const activity = activityOf(entry, label);
      if (activity) out.push(activity);
    }
  }
  return out.reverse();
}

/** What the record's timeline is asked for a filter: its `kinds=`, and for a message channel its
 *  `channel=`, which narrows the messages to texts or to emails. */
export interface TimelineAsk {
  kinds: string;
  channel: "sms" | "email" | undefined;
}

/**
 * The thread's filter as the timeline's `kinds=` and `channel=`, so core-api narrows it rather than
 * the page. Texts and emails are both the `message` kind, told apart by `channel=` (ADR-180), so the
 * SMS filter lists no email and the Email filter no text. Stage moves, callbacks and the platform's
 * own entries are the contact's history and stay in every view, as `filterActivities` keeps events.
 * "" is everything. Null for a channel the record's timeline does not carry: core-api refuses a kind
 * it does not know with a 422.
 */
export function timelineKinds(filter: ActivityFilter): TimelineAsk | null {
  switch (filter) {
    case "all":
      return { kinds: "", channel: undefined };
    case "call":
      return { kinds: "call,stage,system,callback", channel: undefined };
    case "comment":
      return { kinds: "note,stage,system,callback", channel: undefined };
    case "sms":
    case "email":
      return { kinds: "message,stage,system,callback", channel: filter };
    default:
      return null;
  }
}

/** The filters a thread read from the record's timeline offers. */
export const RECORD_FILTERS: readonly ActivityFilter[] = ["all", "sms", "email", "call", "comment"];
/** The filters a thread of one conversation's messages offers: a sender with no record has nothing
 *  else. An email thread with no record (Unassigned) reads its own messages too. */
export const TEXT_FILTERS: readonly ActivityFilter[] = ["all", "sms", "email"];

/**
 * A contact's thread opened from their record (`?lead=`), before any message has made it a
 * conversation: who it is, and nothing the inbox keeps — no owner, followers, star or unread. The
 * activities are not here; the thread reads them from the timeline.
 */
export function recordConversation(card: LeadCard): Conversation {
  const name = [card.first_name, card.last_name].filter(Boolean).join(" ") || null;
  const email = card.email ?? null;
  const field = (key: string, label: string, value: string | null, type?: FieldType): ContactField =>
    ({ key, label, value, type });
  return {
    id: `lead:${card.id}`,
    channel: null,
    leadId: card.id,
    contactName: name,
    phone: card.phone || null,
    email,
    assignee: null,
    followers: [],
    starred: false,
    unread: 0,
    deletedAt: null,
    activities: [],
    contact: {
      owner: null,
      tags: [],
      dnd: { all: card.dnc, calls: card.dnc, sms: card.dnc, whatsapp: card.dnc, imessage: card.dnc, email: card.dnc },
      details: [
        field("name", "Full name", name),
        field("phone", "Phone", card.phone || null, "phone"),
        field("email", "Email", email, "email"),
        field("state", "State", card.state),
        field("language", "Language", card.language),
        field("source", "Source", card.source),
        field("created", "Created", card.created_at.slice(0, 10), "date"),
      ],
    },
  };
}

/* --- from core-api's texts (ADR-095) ---------------------------------------------------------- */

/**
 * A text as a bubble, or an email as its line. Which of our numbers a text was on is the other end
 * from the contact's. An email's words are its preview, never the whole of it, and its "number" is
 * a reply address nobody needs to read, so it carries none: the line is its subject, who and when.
 */
export function messageActivity(m: TextMessage, author: Person | null = null, id = m.id): MessageActivity {
  const inbound = m.direction === "inbound";
  // Who sent a message we sent is its own person; `author` is the timeline entry's, for an older row.
  // A workflow's text or email has nobody: the workflow is carried apart, for the readers who may be
  // told it (ACL-591).
  const sender = m.sent_by
    ? { id: String(m.sent_by.id), name: m.sent_by.name }
    : m.workflow
      ? null
      : author;
  const shared = {
    id,
    at: m.at,
    kind: "message" as const,
    direction: inbound ? ("inbound" as const) : ("outbound" as const),
    messageId: m.id,
    conversationId: m.conversation_id,
    author: inbound ? null : sender,
    status: messageStatus(m.status, inbound),
    error: inbound ? null : m.error_detail ?? m.error,
    sendAt: inbound ? null : (m.send_at ?? null),
  };
  if (m.channel === "email") {
    return {
      ...shared,
      channel: "email",
      body: m.preview ?? "",
      subject: m.subject ?? null,
      label: m.label ?? m.subject ?? null,
      fromContact: m.from_contact ?? null,
      from: m.from_number || null,
      ourNumber: null,
      optOut: false,
      media: [],
      cadence: null,
      workflow: inbound ? null : (m.workflow ?? null),
    };
  }
  const words = textMessageWords(m);
  return {
    ...shared,
    channel: "sms",
    body: m.body,
    subject: null,
    ourNumber: words.ourNumber,
    optOut: m.opt_out,
    media: m.media ?? [],
    cadence: inbound ? null : (m.sent_by_cadence ?? null),
    workflow: inbound ? null : (m.workflow ?? null),
  };
}

/**
 * A row of `GET /conversations` as the inbox's model. The list needs the last text, not the thread:
 * it is the one activity carried, for the row's preview and the type filter. The thread reads the
 * rest when it is opened, from the record's timeline or the conversation's own texts.
 *
 * An email thread (ADR-180) is the lead's address and a reply address of ours: the address is the
 * contact's email, not a phone, and its last text is the last email's summary, drawn as its subject.
 *
 * Owner, followers, tags, star and do-not-disturb are not core-api's yet (an open question on
 * ACL-335): they start empty and stay the page's own.
 */
export function textConversation(t: TextConversation, asRep = false): Conversation {
  const name = t.lead ? [t.lead.first_name, t.lead.last_name].filter(Boolean).join(" ") || null : null;
  const email = t.channel === "email";
  const last: Activity[] =
    // A contact the team has only ever called: core-api derives the row rather than storing one
    // (`channel: "call"`, its id the lead's), because `conversations` is the texts' table. The
    // row's one activity is that call, which is what makes the list's Calls filter and its
    // channel mark true for them — both read `latestActivity`, and before this they read a last
    // text that does not exist.
    t.channel === "call"
      ? [{
          id: `${t.id}:call`,
          at: t.last_message_at,
          kind: "call",
          direction: t.last_direction === "inbound" ? "inbound" : "outbound",
          status: "completed",
          durationSeconds: null,
          agent: null,
          // The list row knows the call happened and when; the leg, its recording and its
          // transcript come from the lead's timeline when the thread is opened, which is the
          // same place the thread body already reads them from.
          callId: null,
          hasRecording: false,
          hasTranscript: false,
          outcome: null,
          summary: "Called",
        } satisfies CallActivity]
      : t.last_direction && t.last_text !== null
      ? [{
          id: `${t.id}:last`,
          at: t.last_message_at,
          kind: "message",
          channel: email ? "email" : "sms",
          direction: t.last_direction,
          body: email ? "" : t.last_text,
          messageId: "",
          conversationId: t.id,
          subject: email ? t.last_text : null,
          author: null,
          status: t.last_direction === "inbound" ? "received" : "sent",
          ourNumber: email ? null : t.our_number,
        } satisfies MessageActivity]
      : [];
  const channel = t.channel === "sms" || t.channel === "email" || t.channel === "call" ? t.channel : null;
  return {
    id: t.id,
    channel,
    leadId: t.lead?.id ?? null,
    contactName: name,
    phone: email ? null : t.contact_number,
    email: email ? t.contact_number || null : null,
    assignee: t.assignee ? { id: String(t.assignee.id), name: t.assignee.name } : null,
    followers: [],
    starred: false,
    unread: t.unread,
    deletedAt: null,
    activities: last,
    contact: {
      owner: null,
      tags: [],
      dnd: { all: false, calls: false, sms: false, whatsapp: false, imessage: false, email: false },
      // An email thread's reply address is ours and of no use to read, so there is no "Texted" row.
      details: email
        ? [
            { key: "name", label: "Full name", value: name },
            { key: "email", label: "Email", value: t.contact_number || null, type: "email" },
            { key: "inbox", label: "Inbox", value: inboxWords(t, asRep) },
          ]
        : [
            { key: "name", label: "Full name", value: name },
            { key: "phone", label: "Phone", value: t.contact_number, type: "phone" },
            { key: "our_number", label: "Texted", value: t.our_number, type: "phone" },
            { key: "inbox", label: "Inbox", value: inboxWords(t, asRep) },
          ],
    },
  };
}


/** Whose inbox a conversation is in, in the panel's words. */
function inboxWords(t: Pick<TextConversation, "campaign_name" | "rep">, asRep: boolean): string {
  // A rep reads no campaign (ACL-652): their own number, or the team's inbox.
  if (asRep) return t.rep ? `${t.rep.name}'s number` : "Team Inbox";
  return t.campaign_name ?? (t.rep ? `${t.rep.name}'s number` : "Unassigned");
}
